import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Textarea } from "../../../ui/textarea";
import { Loader2, Send, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  createProgressReport,
  listProgressReports,
  deleteProgressReport,
  type ProgressReport,
} from "../../../../api/progressReports";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { cn } from "../../../../lib/utils";

interface ProgressReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  takeawayId: string;
  takeawayContent: string;
}

const ProgressReportDialog: React.FC<ProgressReportDialogProps> = ({
  open,
  onOpenChange,
  takeawayId,
  takeawayContent,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const queryClient = useQueryClient();
  const [newContent, setNewContent] = useState("");

  const queryKey = ["progress-reports", takeawayId];

  const { data: reports = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => listProgressReports(retryFetch, takeawayId),
    enabled: open && !!takeawayId,
  });

  const createMutation = useMutation({
    mutationFn: (content: string) =>
      createProgressReport(retryFetch, takeawayId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setNewContent("");
      toast.success(t("progress_report_saved", "Progress update saved"));
    },
    onError: () => {
      toast.error(t("progress_report_error", "Failed to save progress update"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgressReport(retryFetch, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error(t("delete_error", "Failed to delete"));
    },
  });

  const handleSubmit = () => {
    const trimmed = newContent.trim();
    if (!trimmed) return;
    createMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {t("progress_report", "Progress Update")}
          </DialogTitle>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
            {takeawayContent}
          </p>
        </DialogHeader>

        {/* New report input */}
        <div className="space-y-2">
          <Textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t(
              "progress_report_placeholder",
              "What have you done towards this goal?"
            )}
            rows={3}
            className="resize-none"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={!newContent.trim() || createMutation.isPending}
              onClick={handleSubmit}
              className="gap-1.5"
            >
              {createMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              {t("add_progress_report", "Add progress update")}
            </Button>
          </div>
        </div>

        {/* Reports list */}
        <div className="flex-1 overflow-y-auto space-y-3 mt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t("no_progress_reports", "No progress updates yet")}
            </p>
          ) : (
            reports.map((report: ProgressReport) => (
              <div
                key={report._id}
                className="group relative rounded-lg border bg-card p-4 transition-colors hover:bg-muted/30"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap flex-1">
                    {report.content}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-7 w-7 p-0 flex-shrink-0",
                      "opacity-0 group-hover:opacity-100 transition-opacity",
                      "text-muted-foreground hover:text-destructive"
                    )}
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(report._id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground/60 mt-2">
                  {new Date(report.createdOn).toLocaleDateString(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProgressReportDialog;
